import { KnownApp } from "@kwp/contracts";
import { paths } from "@kwp/firestore";
import { useMutation } from "@tanstack/react-query";
import { doc, onSnapshot } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { useTRPC } from "../../lib/api";
import { useClients } from "../../lib/clients";
import type { MailKindView } from "./kinds";
import type { Box } from "./mailboxes";
import type { Thread } from "./model";

/**
 * Apps kept out of All mail (architecture §6.7, WP-066): the person's own choice, on
 * `users/{uid}.hiddenInAllMail`, set from the eye button beside GitHub in the sidebar. Only All
 * mail leaves their mail out; GitHub's own view and Notifications still list it.
 */
export type HiddenApps = readonly KnownApp[];

const Stored = z.object({ hiddenInAllMail: z.array(KnownApp).default([]) });

/** Whether this view is All mail in the inbox, the one view the choice applies to. */
export function hidesApps(box: Box, filter: string | undefined, mailKind: MailKindView, sourced: boolean): boolean {
  return box === "inbox" && filter === undefined && mailKind === "all" && !sourced;
}

/** The threads left once the hidden apps' mail is taken out. */
export function withoutHidden(threads: readonly Thread[], hidden: HiddenApps): Thread[] {
  if (hidden.length === 0) return [...threads];
  return threads.filter((t) => t.source === null || !hidden.includes(t.source.app));
}

/** All mail's unread count with the hidden apps' unread taken out; null until both are known. */
export function allCountWithout(all: number | null, hiddenUnread: ReadonlyArray<number | null>): number | null {
  if (all === null) return null;
  let n = all;
  for (const h of hiddenUnread) {
    if (h === null) return null;
    n -= h;
  }
  return Math.max(0, n);
}

/**
 * The caller's hidden apps, live from their user document (the owner may read it; only the api
 * writes it), and the switch. Until the first snapshot nothing is hidden, so the list never waits
 * on it; `pending` holds a choice the api has not confirmed yet so the button answers at once.
 */
export function useHiddenApps(uid: string): { hidden: HiddenApps; setHidden: (app: KnownApp, hidden: boolean) => void } {
  const { firebase } = useClients();
  const trpc = useTRPC();
  const [stored, setStored] = useState<HiddenApps>([]);
  const [pending, setPending] = useState<HiddenApps | null>(null);
  useEffect(
    () =>
      onSnapshot(
        doc(firebase.db, paths.user(uid)),
        (snap) => {
          const parsed = Stored.safeParse(snap.exists() ? snap.data() : {});
          if (parsed.success) setStored(parsed.data.hiddenInAllMail);
        },
        () => undefined,
      ),
    [firebase.db, uid],
  );
  const save = useMutation(
    trpc.people.setHiddenInAllMail.mutationOptions({
      onSuccess: (out) => {
        setStored(out.hiddenInAllMail);
        setPending(null);
      },
      onError: () => setPending(null),
    }),
  );
  const { mutate } = save;
  const hidden = pending === null ? stored : pending;
  const setHidden = useCallback(
    (app: KnownApp, hide: boolean) => {
      setPending(hide ? [...new Set([...hidden, app])] : hidden.filter((a) => a !== app));
      mutate({ app, hidden: hide });
    },
    [hidden, mutate],
  );
  return { hidden, setHidden };
}
